import React, { useMemo } from "react";
import { useBarcode } from "next-barcode";
import { useSelector } from "react-redux";

const BarcodeLabel = ({
  itemPrice = 0,
  itemName = "",
  value = "",
  backgroundColor = "#ffffff",
  labelWidth = 38,
  labelHeight = 25,
  batteryHealth,
  category,
  showPrice = true,
  showBatteryHealth = true,
  deviceMode = 'both',
}) => {
  const { inputRef } = useBarcode({
    value: value || " ",
    options: {
      background: backgroundColor,
      width: 1.2,
      height: 35,
      displayValue: false,
      margin: 0,
    },
  });

  const { companies } = useSelector((state) => state.companyReducer);

  const companyName = useMemo(() => {
    const company = companies?.find((comp) => comp.company_name);
    return company ? company.company_name : "Ishop Master";
  }, [companies]);

  const isDevice = useMemo(() => {
    return category?.toLowerCase() === "device";
  }, [category]);

  const itemNameFontSize = useMemo(() => {
    const len = itemName?.length || 0;
    if (len > 45) return "1.7mm";
    if (len > 30) return "2.0mm";
    if (len > 18) return "2.3mm";
    return "2.6mm";
  }, [itemName]);

  const parsedPrice = useMemo(() => {
    const num = Number(itemPrice);
    return isNaN(num) ? 0 : num;
  }, [itemPrice]);

  return (
    <div
      className="label"
      style={{
        width: `${labelWidth}mm`,
        height: `${labelHeight}mm`,
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        padding: "0.8mm 1.2mm",
        backgroundColor: backgroundColor,
        boxSizing: "border-box",
        border: "none",
        margin: 0,
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Arial, sans-serif',
      }}
    >
      {/* 1. Header: Item & Variant Name (Supports 2 Lines) */}
      <div
        className="labelHeader"
        style={{
          height: `${labelHeight * 0.32}mm`,
          maxHeight: `${labelHeight * 0.32}mm`,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          textAlign: "center",
          overflow: "hidden",
          textTransform: "uppercase",
        }}
      >
        <div
          style={{
            fontWeight: "900",
            fontSize: itemNameFontSize,
            lineHeight: 1.05,
            width: "100%",
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
            textOverflow: "ellipsis",
            wordBreak: "break-word",
          }}
        >
          {itemName}
        </div>
        {showBatteryHealth && isDevice && (
          <div style={{ fontSize: "1.6mm", fontWeight: "bold", borderTop: "0.1mm solid #0002", width: "100%", marginTop: "0.2mm" }}>
            B/H: {batteryHealth || 0}%
          </div>
        )}
      </div>

      {/* 2. Middle: Barcode Image + Serial/Barcode Number */}
      <div
        style={{
          height: `${labelHeight * 0.48}mm`,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          overflow: "hidden",
        }}
      >
        <img
          ref={inputRef}
          alt={`Barcode for ${value}`}
          style={{
            width: "98%",
            maxHeight: `${labelHeight * 0.34}mm`,
            objectFit: "fill",
          }}
        />
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: "2.1mm",
            fontWeight: "900",
            lineHeight: 1,
            textAlign: "center",
            width: "100%",
            fontFamily: "monospace",
            marginTop: "0.3mm",
            letterSpacing: "0.2px",
          }}
        >
          {isDevice ? `IMEI: ${value}` : `SN: ${value}`}
        </div>
      </div>

      {/* 3. Footer: Price & Company Branding */}
      <div
        style={{
          height: `${labelHeight * 0.18}mm`,
          width: "100%",
          display: "flex",
          justifyContent: showPrice ? "space-between" : "flex-end",
          alignItems: "flex-end",
          padding: "0 0.2mm",
          boxSizing: "border-box",
          lineHeight: 1,
        }}
      >
        {showPrice && (
          <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", lineHeight: 1 }}>
            <span style={{ fontSize: "1.3mm", opacity: 0.85, fontWeight: "700" }}>RS.</span>
            <span style={{ fontSize: "2.5mm", fontWeight: "900", lineHeight: 1 }}>
              {parsedPrice.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </span>
          </div>
        )}

        <div
          style={{
            textAlign: "right",
            display: "flex",
            alignItems: "flex-end",
            justifyContent: "flex-end",
            textTransform: "uppercase",
            lineHeight: 1,
            whiteSpace: "nowrap",
          }}
        >
          <span style={{ fontSize: "1.8mm", fontWeight: "900", letterSpacing: "0.2px" }}>{companyName}</span>
        </div>
      </div>
    </div>
  );
};

export default BarcodeLabel;